import {
  confirmPasswordReset,
  getAuth,
  verifyPasswordResetCode,
} from "firebase/auth";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";

import { CentralAuthShell } from "../src/components/CentralAuthShell";
import { Button, ErrorState, Field } from "../src/components/ui";
import { firebaseApp } from "../src/lib/firebase";
import { central, spacing } from "../src/theme/tokens";

export default function PasswordResetScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ oobCode?: string | string[] }>();
  const auth = useMemo(() => getAuth(firebaseApp), []);
  const code = Array.isArray(params.oobCode) ? params.oobCode[0] : params.oobCode;
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!code) {
      setBusy(false);
      setError("This password reset link is missing or invalid.");
      return () => { active = false; };
    }
    void verifyPasswordResetCode(auth, code)
      .then((verifiedEmail) => { if (active) setEmail(verifiedEmail); })
      .catch(() => { if (active) setError("This password reset link has expired or was already used."); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [auth, code]);

  const submit = async () => {
    if (!code || password.length < 8 || password !== confirmation) return;
    setSaving(true);
    setError(null);
    try {
      await confirmPasswordReset(auth, code, password);
      setComplete(true);
    } catch (caught) {
      setError(passwordResetError(caught));
    } finally {
      setSaving(false);
    }
  };

  return (
    <CentralAuthShell>
      <View style={styles.panel}>
        {complete ? (
          <>
            <View style={styles.copy}>
              <Text accessibilityRole="header" style={styles.title}>Password changed</Text>
              <Text style={styles.body}>Your password has been updated. You can now sign in to WeHonor Central.</Text>
            </View>
            <Button fullWidth label="Go to Central sign in" onPress={() => router.replace("/signed-out" as never)} shape="rounded" />
          </>
        ) : (
          <>
            <View style={styles.copy}>
              <Text accessibilityRole="header" style={styles.title}>Set a new password</Text>
              <Text style={styles.body}>{email ? `Choose a new password for ${email}.` : "Choose a new password for your Central account."}</Text>
            </View>
            {busy ? <Text style={styles.body}>Checking your secure reset link…</Text> : null}
            {error ? <ErrorState title="Password reset unavailable" description={error} /> : null}
            {!busy && !error ? (
              <>
                <Field autoComplete="new-password" label="New password" onChangeText={setPassword} secureTextEntry textContentType="newPassword" value={password} />
                <Field autoComplete="new-password" label="Confirm new password" onChangeText={setConfirmation} onSubmitEditing={() => void submit()} returnKeyType="done" secureTextEntry textContentType="newPassword" value={confirmation} />
                <Text style={styles.hint}>Use at least 8 characters. Both passwords must match.</Text>
                <Button disabled={password.length < 8 || password !== confirmation} fullWidth label="Change password" loading={saving} onPress={() => void submit()} shape="rounded" />
              </>
            ) : null}
            {error ? <Button label="Return to Central sign in" onPress={() => router.replace("/signed-out" as never)} shape="rounded" variant="secondary" /> : null}
          </>
        )}
      </View>
    </CentralAuthShell>
  );
}

function passwordResetError(error: unknown) {
  const code = typeof error === "object" && error !== null && "code" in error && typeof error.code === "string"
    ? error.code
    : "";
  if (code === "auth/expired-action-code") return "This reset link has expired. Request a new link and open it right away.";
  if (code === "auth/invalid-action-code") return "This reset link is no longer valid or was already used. Request a new link.";
  if (code === "auth/weak-password") return "That password does not meet the account’s password requirements. Use a stronger password and try again.";
  if (code === "auth/user-disabled") return "This Central account is disabled. Contact your administrator for help.";
  if (code === "auth/network-request-failed") return "The password update could not reach Firebase. Check the internet connection and try again.";
  if (code === "auth/too-many-requests") return "Too many password-reset attempts were made. Wait a few minutes, then request a new link.";
  if (code === "auth/operation-not-allowed") return "Password sign-in is not enabled for this Central account. Contact your administrator.";
  if (code === "auth/invalid-credential") return "Firebase rejected this reset credential. Request a new reset link and open the newest email.";
  return code
    ? `We couldn’t change your password (Firebase: ${code.replace(/^auth\//, "")}). Request a new reset link and try again.`
    : "We couldn’t change your password. Request a new reset link and try again.";
}

const styles = StyleSheet.create({
  panel: { gap: spacing.lg, width: "100%" },
  copy: { gap: spacing.sm },
  title: { color: central.text.primary, fontFamily: central.typography.display, fontSize: 30, lineHeight: 36 },
  body: { color: central.text.secondary, fontFamily: central.typography.body, fontSize: 15, lineHeight: 23 },
  hint: { color: central.text.muted, fontFamily: central.typography.body, fontSize: 12, lineHeight: 18 },
});
